import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import ShareOutlinedIcon from "@material-ui/icons/ShareOutlined";
import PhoneOutlinedIcon from "@material-ui/icons/PhoneOutlined";
import CheckIcon from "@material-ui/icons/Check";
import AddIcon from "@material-ui/icons/Add";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

import { workforceApi } from "src/actions/workforce.actions";

// ─── Tokens (shared with the host dashboard) ─────────────────────────────────
const TOKENS = {
  bg: "#F5F6FA",
  surface: "#FFFFFF",
  border: "#EEF0F5",
  borderStrong: "#E5E7EB",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  divider: "#F1F5F9",
};

const AGENT_THEME = {
  tim: { bg: "#F0F9FF", fg: "#0EA5E9", Icon: PeopleOutlineIcon },
  david: { bg: "#ECFDF5", fg: "#10B981", Icon: MailOutlineIcon },
  lora: { bg: "#FAF5FF", fg: "#A21CAF", Icon: ShareOutlinedIcon },
  milli: { bg: "#F1F5F9", fg: "#64748B", Icon: PhoneOutlinedIcon },
};
const agentTheme = (code) => AGENT_THEME[code] || AGENT_THEME.milli;

const STATUS_STYLE = {
  active: { bg: "#ECFDF5", fg: "#10B981", label: "Active" },
  working: { bg: "#FFFBEB", fg: "#D97706", label: "Working" },
  paused: { bg: "#F1F5F9", fg: "#64748B", label: "Paused" },
};

const useStyles = makeStyles((theme) => ({
  page: {
    backgroundColor: TOKENS.bg,
    minHeight: "100%",
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(6),
  },
  loadingWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
    gap: theme.spacing(2),
    color: TOKENS.textSecondary,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: theme.spacing(1),
  },
  backBtn: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.borderStrong}`,
    borderRadius: 10,
    width: 40,
    height: 40,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: TOKENS.textPrimary,
    lineHeight: 1.2,
  },
  subtitle: {
    fontSize: 14,
    color: TOKENS.textSecondary,
    marginBottom: theme.spacing(3),
  },

  sectionHeading: {
    fontSize: 18,
    fontWeight: 700,
    color: TOKENS.textPrimary,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(4),
  },

  // Cards
  card: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    padding: theme.spacing(3),
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  cardTop: { display: "flex", alignItems: "center", gap: 12 },
  iconWrap: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  agentName: { fontSize: 16, fontWeight: 700, color: TOKENS.textPrimary },
  agentRole: { fontSize: 13, color: TOKENS.textSecondary },
  statusBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: 999,
    marginLeft: "auto",
  },
  description: {
    fontSize: 13,
    color: TOKENS.textSecondary,
    marginTop: theme.spacing(2),
    lineHeight: 1.5,
  },
  capList: { marginTop: theme.spacing(2), marginBottom: theme.spacing(2) },
  capRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 6,
  },
  capText: { fontSize: 12.5, color: TOKENS.textSecondary, lineHeight: 1.4 },

  // Footers
  activatedFooter: {
    marginTop: "auto",
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${TOKENS.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  toggleBtn: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    borderRadius: 10,
    borderColor: TOKENS.borderStrong,
    color: TOKENS.textPrimary,
  },
  openLink: {
    fontSize: 13,
    fontWeight: 600,
    color: TOKENS.accent,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  activateBtn: {
    marginTop: "auto",
    textTransform: "none",
    fontWeight: 600,
    fontSize: 14,
    borderRadius: 10,
    backgroundColor: TOKENS.accent,
    color: "#FFF",
    boxShadow: "none",
    padding: "8px 0",
    "&:hover": { backgroundColor: "#000000", boxShadow: "none" },
  },
  pendingPill: {
    fontSize: 11,
    fontWeight: 700,
    color: "#B45309",
    backgroundColor: "#FFFBEB",
    border: "1px solid #FDE68A",
    borderRadius: 999,
    padding: "2px 9px",
    marginTop: 8,
    display: "inline-block",
  },
  emptyCard: {
    backgroundColor: TOKENS.surface,
    border: `1px dashed ${TOKENS.borderStrong}`,
    borderRadius: 16,
    padding: theme.spacing(4),
    textAlign: "center",
    color: TOKENS.textMuted,
    fontSize: 13.5,
  },
  removeText: {
    color: "#DC2626",
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
  },
}));

const WorkforceAgents = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]); // activated subscriptions (full detail)
  const [available, setAvailable] = useState([]); // catalog items not yet activated
  const [busyId, setBusyId] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [agents, catalog] = await Promise.all([
        workforceApi.getAgents(),
        workforceApi.getCatalog(),
      ]);
      setTeam(agents || []);
      setAvailable((catalog || []).filter((a) => !a.activated));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to load agents", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleActivate = async (code) => {
    setBusyId(code);
    try {
      await workforceApi.activateAgent(code);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const handleToggle = async (agent) => {
    setBusyId(agent.id);
    try {
      await workforceApi.toggleAgent(agent.id);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setBusyId(removeTarget.id);
    try {
      await workforceApi.removeAgent(removeTarget.id);
      setRemoveTarget(null);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Box className={classes.page}>
        <Box className={classes.loadingWrap}>
          <CircularProgress size={32} style={{ color: TOKENS.accent }} />
          <Typography>Loading your team…</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Tubayo Host | Manage your team</title>
      </Helmet>

      <Box className={classes.page}>
        <Container maxWidth={false} style={{ maxWidth: 1280 }}>
          {/* Header */}
          <Box className={classes.header}>
            <IconButton
              className={classes.backBtn}
              onClick={() => navigate("/app/workforce")}
            >
              <ArrowBackIcon
                style={{ fontSize: 20, color: TOKENS.textSecondary }}
              />
            </IconButton>
            <Typography className={classes.title}>Manage your team</Typography>
          </Box>
          <Typography className={classes.subtitle}>
            Activate the agents you want working for you. Pause or remove them
            anytime.
          </Typography>

          {/* Your team */}
          <Typography className={classes.sectionHeading}>
            Your team ({team.length})
          </Typography>
          {team.length === 0 ? (
            <Box className={classes.emptyCard}>
              No agents yet. Activate one below to get started.
            </Box>
          ) : (
            <Grid container spacing={3}>
              {team.map((agent) => {
                const def = agent.definition || {};
                const theme = agentTheme(def.code);
                const AgentIcon = theme.Icon;
                const status =
                  STATUS_STYLE[agent.status] ||
                  (agent.is_active ? STATUS_STYLE.active : STATUS_STYLE.paused);
                return (
                  <Grid key={agent.id} item xs={12} sm={6} md={4}>
                    <Box className={classes.card}>
                      <Box className={classes.cardTop}>
                        <Box
                          className={classes.iconWrap}
                          style={{ backgroundColor: theme.bg, color: theme.fg }}
                        >
                          <AgentIcon />
                        </Box>
                        <Box style={{ minWidth: 0 }}>
                          <Typography className={classes.agentName}>
                            {def.name}
                          </Typography>
                          <Typography className={classes.agentRole}>
                            {def.role}
                          </Typography>
                        </Box>
                        <span
                          className={classes.statusBadge}
                          style={{
                            backgroundColor: status.bg,
                            color: status.fg,
                          }}
                        >
                          {status.label}
                        </span>
                      </Box>

                      <Typography className={classes.description}>
                        {agent.last_action || "Waiting for next task"}
                      </Typography>
                      {agent.pending_approvals > 0 && (
                        <span className={classes.pendingPill}>
                          {agent.pending_approvals} to review
                        </span>
                      )}

                      <Box className={classes.activatedFooter}>
                        <Box
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            className={classes.toggleBtn}
                            disabled={busyId === agent.id}
                            startIcon={
                              agent.is_active ? (
                                <PauseCircleOutlineIcon />
                              ) : (
                                <PlayCircleOutlineIcon />
                              )
                            }
                            onClick={() => handleToggle(agent)}
                          >
                            {agent.is_active ? "Pause" : "Resume"}
                          </Button>
                          <Tooltip title="Remove from team">
                            <IconButton
                              size="small"
                              onClick={() => setRemoveTarget(agent)}
                            >
                              <DeleteOutlineIcon
                                style={{ fontSize: 20, color: "#DC2626" }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <span
                          className={classes.openLink}
                          onClick={() =>
                            navigate(`/app/workforce/agents/${agent.id}`)
                          }
                        >
                          Open <ArrowForwardIcon style={{ fontSize: 16 }} />
                        </span>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Available to add */}
          <Typography className={classes.sectionHeading}>
            Available to add
          </Typography>
          {available.length === 0 ? (
            <Box className={classes.emptyCard}>
              You've activated every available agent. New agents will appear
              here.
            </Box>
          ) : (
            <Grid container spacing={3}>
              {available.map((agent) => {
                const theme = agentTheme(agent.code);
                const AgentIcon = theme.Icon;
                const caps = (agent.capabilities || []).slice(0, 3);
                return (
                  <Grid key={agent.code} item xs={12} sm={6} md={4}>
                    <Box className={classes.card}>
                      <Box className={classes.cardTop}>
                        <Box
                          className={classes.iconWrap}
                          style={{ backgroundColor: theme.bg, color: theme.fg }}
                        >
                          <AgentIcon />
                        </Box>
                        <Box style={{ minWidth: 0 }}>
                          <Typography className={classes.agentName}>
                            {agent.name}
                          </Typography>
                          <Typography className={classes.agentRole}>
                            {agent.role}
                          </Typography>
                        </Box>
                      </Box>

                      {agent.description && (
                        <Typography className={classes.description}>
                          {agent.description}
                        </Typography>
                      )}

                      {caps.length > 0 && (
                        <Box className={classes.capList}>
                          {caps.map((cap, i) => (
                            <Box key={i} className={classes.capRow}>
                              <CheckIcon
                                style={{
                                  fontSize: 16,
                                  color: theme.fg,
                                  marginTop: 1,
                                }}
                              />
                              <span className={classes.capText}>{cap}</span>
                            </Box>
                          ))}
                        </Box>
                      )}

                      <Button
                        variant="contained"
                        fullWidth
                        disableElevation
                        className={classes.activateBtn}
                        disabled={busyId === agent.code}
                        startIcon={
                          busyId === agent.code ? (
                            <CircularProgress
                              size={16}
                              style={{ color: "#FFF" }}
                            />
                          ) : (
                            <AddIcon />
                          )
                        }
                        onClick={() => handleActivate(agent.code)}
                      >
                        {busyId === agent.code ? "Activating…" : "Activate"}
                      </Button>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Container>
      </Box>

      {/* Remove confirmation */}
      <Dialog
        open={Boolean(removeTarget)}
        onClose={() => setRemoveTarget(null)}
        PaperProps={{ style: { borderRadius: 16, padding: 8, maxWidth: 420 } }}
      >
        <DialogTitle>
          Remove {removeTarget?.definition?.name} from your team?
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            style={{ fontSize: 14, color: TOKENS.textSecondary }}
          >
            This stops the agent and deletes its drafts and activity history.
            Leads it captured will remain in your Data Hub. You can re-activate
            it later.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "8px 16px 16px" }}>
          <Button
            onClick={() => setRemoveTarget(null)}
            style={{
              textTransform: "none",
              fontWeight: 600,
              color: TOKENS.textSecondary,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemove}
            disabled={busyId === removeTarget?.id}
            variant="contained"
            disableElevation
            style={{
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "#DC2626",
              color: "#FFF",
              borderRadius: 10,
            }}
          >
            {busyId === removeTarget?.id ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WorkforceAgents;
